import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RefreshCw, MapPin, Euro, Clock, ChevronRight, Loader2, Crown, Shield, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { jobCoverSrc, categoryCover } from "../../lib/covers";

function MatchBar({ score }) {
  const color = score >= 85 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-700 w-9 text-right">{score}%</span>
    </div>
  );
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";
}

function RecoThumb({ job, isGold }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const logoUrl = job?.company_id ? `${backend}/api/companies/${job.company_id}/logo` : null;
  const [logoFailed, setLogoFailed] = useState(false);
  const [coverSrc, setCoverSrc] = useState(() => jobCoverSrc(job));
  const fallback = categoryCover(job?.category, job?.id || job?.slug);
  const border = isGold ? "border-amber-400/70" : "border-black/10";

  return (
    <div className={`relative w-24 h-20 sm:w-28 sm:h-24 shrink-0 rounded-lg border ${border} overflow-hidden bg-slate-100`}>
      <img
        src={coverSrc}
        alt={`Foto ${job?.category || "vaga"}`}
        loading="lazy"
        onError={() => { if (coverSrc !== fallback) setCoverSrc(fallback); }}
        className="w-full h-full object-cover"
      />
      {/* Logo sobreposto no canto (pequeno badge) */}
      {logoUrl && !logoFailed && (
        <div className="absolute bottom-1 left-1 w-7 h-7 rounded-md bg-white shadow ring-1 ring-black/10 p-0.5 overflow-hidden">
          <img
            src={logoUrl}
            alt=""
            className="w-full h-full object-contain"
            onError={() => setLogoFailed(true)}
          />
        </div>
      )}
    </div>
  );
}

export function RecommendationsSection({ limit = 6, compact = false, onExploreAll, onCount }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/candidate/recommendations?limit=${limit}`);
      setItems(data.recommendations || []);
      if (onCount) onCount(data.recommendations?.length || 0);
    } catch (e) {
      toast.error(formatErr(e));
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [limit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      data-testid="recommendations-section"
      className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 sm:p-6">
      <div className="flex items-center justify-between mb-1 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading font-black tracking-tighter text-lg text-slate-900">Recomendado para ti</h2>
            <p className="text-xs text-slate-500">Ranqueado pelo teu perfil, distrito e CV</p>
          </div>
        </div>
        <Button
          data-testid="recommendations-refresh"
          variant="ghost" size="sm" className="rounded-full text-slate-500 hover:text-slate-900"
          onClick={load} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading && !items && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> A calcular o teu match…
        </div>
      )}

      {items && items.length === 0 && (
        <div className="py-8 text-center text-sm text-slate-500">
          Ainda não há vagas com match. Completa o perfil e carrega o CV para veres recomendações.
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="mt-4 space-y-2.5">
          {items.map((j) => {
            const tier = j.boost?.tier;
            const isGold = tier === "gold" || tier === "destaque_max";
            const isSilver = tier === "silver" || tier === "destaque";
            const isExpress = tier === "express";
            const accent = isGold ? "before:bg-amber-400" : isSilver ? "before:bg-sky-400" : isExpress ? "before:bg-orange-400" : "before:bg-transparent";
            return (
              <li key={j.id} data-testid={`recommendation-${j.slug}`}>
                <Link
                  to={`/empregos/${j.slug}`}
                  className={`group flex items-start gap-3 relative rounded-xl border border-black/5 hover:border-slate-200 hover:shadow-sm transition p-3 overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${accent}`}
                >
                  <RecoThumb job={j} isGold={isGold} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">{j.company_name}</p>
                        <h3 className="font-heading font-bold tracking-tight text-slate-900 group-hover:text-amber-600 transition line-clamp-2 text-sm mt-0.5">{j.title}</h3>
                      </div>
                      {isGold && <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase bg-amber-400 text-slate-950"><Crown className="w-3 h-3" />Gold</span>}
                      {isSilver && <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase bg-sky-500 text-white"><Shield className="w-3 h-3" />Silver</span>}
                      {isExpress && <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase bg-orange-500 text-white"><Zap className="w-3 h-3" />Express</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600 mt-1">
                      <span className="inline-flex items-center gap-1 truncate max-w-full"><MapPin className="w-3 h-3 shrink-0" />{j.concelho}, {j.district}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{j.regime}</span>
                      {j.salary_min && (
                        <span className="inline-flex items-center gap-1 font-semibold text-slate-700"><Euro className="w-3 h-3" />{j.salary_min}€{j.salary_max ? `–${j.salary_max}€` : "+"}</span>
                      )}
                    </div>
                    <div className="mt-2">
                      <MatchBar score={j.match_score} />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!compact && items && items.length > 0 && onExploreAll && (
        <div className="mt-4 pt-3 border-t border-black/5">
          <Button variant="ghost" className="w-full justify-between rounded-full text-sm" onClick={onExploreAll}>
            Ver todas as vagas <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </section>
  );
}
